import { difference, uniq } from "lodash";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSetState } from "react-use";
import { AuthZGuidedTourState } from "src/components/authz-tour-guide/utils";
import { fetchUserRoles } from "src/pages/users/userDetail/service";
import { AlertMessage } from "../../../../../../common/data/AlertMsg";
import {
  isNullOrEmptyString,
  isNullOrUndefined,
  isNullOrUndefinedOrEmpty,
} from "../../../../../../common/utils";
import { Nullable } from "../../../../../../common/utils/nullable";
import {
  ColumnRepresentationDTO,
  ColumnRepresentations,
} from "../../../../../../components/column-representations/model/ColumnRepresentations";
import { errorMsg, successMsg } from "../../../../../../components/simpleMessageAlert";
import { IsFeatureEnabledOrNot } from "../../../../../../FeatureToggles/FeatureEnabledOrNot";
import { ToggleList } from "../../../../../../FeatureToggles/toggleList";
import { RootState } from "../../../../../../redux/store";
import {
  ApplicationDetailsActions,
  getSelectedColumns,
  getTotalColumns,
  showMyTasksFilter,
} from "../../../model";
import {
  AppReviewTasksActions,
  findBatchSelectedReviewTaskKeysByReviewId,
  findEditedReviewTasksByReviewId,
  findReviewById,
  findSubmittedReviewByReviewId,
  isMyContent,
  isNotAllMyContent,
} from "./model/model";
import {
  PostBatchClearReviewTaskAction,
  PostBatchSaveReviewTaskAction,
  PostBatchSubmitReviewTaskAction,
  PostBatchTasksReassignmentAction,
  PostReviewTaskActions,
} from "./model/PostReviewTaskAction";
import { ReviewTask, ReviewTasks } from "./model/ReviewTasks";
import { UpdateReviewTaskActions } from "./model/UpdateReviewTasks";
import * as service from "./service";
import { getTourStepsForReviewTasksTab } from "./tour-guide-helper";

export enum FilterKeys {
  ShowAll = "showAll",
  ShowMyTasks = "ShowMyTasks",
  ShowSavedDecision = "showSavedDecision",
  ShowPendingDecisions = "showPendingDecisions",
  ShowSubmittedDecisions = "showSubmittedDecisions",
}

export type Filters = {
  [FilterKeys.ShowAll]: boolean,
  [FilterKeys.ShowMyTasks]: boolean,
  [FilterKeys.ShowPendingDecisions]: boolean,
  [FilterKeys.ShowSavedDecision]: boolean,
  [FilterKeys.ShowSubmittedDecisions]: boolean,
}

const showMyTasksFilterState = {
  [FilterKeys.ShowAll]: false,
  [FilterKeys.ShowMyTasks]: true,
  [FilterKeys.ShowPendingDecisions]: false,
  [FilterKeys.ShowSavedDecision]: false,
  [FilterKeys.ShowSubmittedDecisions]: false,
};

const showAllTasksFilterState = {
  [FilterKeys.ShowAll]: true,
  [FilterKeys.ShowMyTasks]: false,
  [FilterKeys.ShowPendingDecisions]: false,
  [FilterKeys.ShowSavedDecision]: false,
  [FilterKeys.ShowSubmittedDecisions]: false,
};

export const ReviewTasksController = (appId, reviewId, activeTabId, isReviewInProgress: boolean) => {
  const dispatch = useDispatch();
  const [automaticallyRemovedColumns, setAutomaticallyRemovedColumns] = useState([]);
  const pinnedColumnsList = ["User", "Review decisions & Reasoning", "Delegation"];
  const [postReviewTaskType, setPostReviewTaskType] = useState<Nullable<PostReviewTaskActions>>(null);
  const {
    value: { data: userDetails },
  } = useSelector((rootState: RootState) => rootState.userDetail.userDetail);
  const [ignoredReviewTasks, setIgnoredReviewTasks] = useState([]);
  const isSaveAsDraftFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.SaveAsDraft);
  const reviewInFullScreenFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.ReviewInFullScreen);
  const showMyListFilter = useSelector((rootState: RootState) =>
    showMyTasksFilter(rootState.appDetails, appId, activeTabId),
  );
  const {
    value: { data: appsList },
  } = useSelector((rootState: RootState) => rootState.applicationList.applicationList);

  const [hoveredTaskIds, setHoveredTaskIds] = useState<string[]>([]);
  const isExportTasksFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.ExportMyTasks);

  const [isPreviewModalVisible, setPreviewModalVisible] = useState(false);
  const [filters, setFilters] = useState<Filters>(showAllTasksFilterState);
  const [tableScrollPosition, setTableScrollPosition] = useState<number>(0);
  const [noOfIgnoredRows, setNoOfIgnoredRows] = useState(0);

  const isGuidedTourFeatureEnabled = IsFeatureEnabledOrNot(ToggleList.GuidedTourForReview);

  function getTableBodyElement(): HTMLElement | null {
    if (document.getElementById(`rc-tabs-0-panel-${reviewId}`)) {
      return document.getElementById(`rc-tabs-0-panel-${reviewId}`)!!.querySelector("div.ant-table-body")!!;
    }
    //this is done to make the tests pass
    if (document.getElementById(`rc-tabs-test-panel-${reviewId}`)) {
      return document.getElementById(`rc-tabs-test-panel-${reviewId}`)!!.querySelector("div.ant-table-body")!!;
    }
    return null;
  }

  useEffect(() => {
    getTableBodyElement()?.addEventListener("scroll", trackTableScroll);
    return () => {
      getTableBodyElement()?.removeEventListener("scroll", trackTableScroll);
    };
  }, []);

  function trackTableScroll(_event) {
    setTableScrollPosition(getTableBodyElement()!!.scrollTop);
  }

  function scrollToLastUpdatedPositionInTable() {
    if (getTableBodyElement() !== null) {
      getTableBodyElement()!!.scrollTop = tableScrollPosition;
    }
  }

  let columns: ColumnRepresentations = new ColumnRepresentations([]);
  let reviewTasks: ReviewTasks = new ReviewTasks([]);
  let initialReviewLoading = null;
  const review = useSelector((rootState: RootState) => findReviewById(rootState.appReviewTasks, reviewId));
  let selectedColumns = useSelector((rootState: RootState) =>
    getSelectedColumns(rootState.appDetails, appId),
  );
  const totalColumns = useSelector((rootState: RootState) =>
      getTotalColumns(rootState.appDetails, appId),
  );
  const previewReviewTasksPEV = useSelector((rootState: RootState) => rootState.appReviewTasks.previewReviewTasks);
  const [previewReviewTasks, setPreviewReviewTasks] = useState(new ReviewTasks(previewReviewTasksPEV.value.dataSource));

  useEffect(() => {
    setPreviewReviewTasks(new ReviewTasks(previewReviewTasksPEV.value.dataSource));
  }, [previewReviewTasksPEV.value.dataSource]);

  if (review) {
    columns = new ColumnRepresentations(review.reviewTasks.value.columns);
    reviewTasks = new ReviewTasks(review.reviewTasks.value.dataSource);
    initialReviewLoading = review.reviewTasks.progress;
  }

  const editedReviewTasks = useSelector((rootState: RootState) =>
    findEditedReviewTasksByReviewId(rootState.appReviewTasks, reviewId),
  );

  const [tourState, setTourState] = useSetState<AuthZGuidedTourState>({
    run: false,
    steps: [],
  });
  const [isTasksLoadedFirstTime, setIsTasksLoadedFirstTime] = useState<boolean>(false);

  const [isUserRoleApiLoaded, setIsUserRoleApiLoaded] = useState<boolean>(false);
  const [isAoOrGaa, setIsAoOrGaa] = useState(false);

  useEffect(() => {
    fetchUserRoles(appId).then((response) => {
      if (response.globalAccessApprover || response.accessOwner) {
        setIsAoOrGaa(true);
      }
      setIsUserRoleApiLoaded(true);
    });
  }, [appId]);

  useEffect(() => {
    if (isTasksLoadedFirstTime && isUserRoleApiLoaded) {
      setTourState({
        run: isGuidedTourFeatureEnabled && isReviewInProgress,
        steps: getTourStepsForReviewTasksTab(reviewId, {
          isBulkReviewTaskSelectionFeaturePresent: reviewTasks.shouldShowSubmitNotice(),
          isReviewDecisionAndReasoningFeaturePresent: reviewTasks.viewableTasks(filters).length > 0 && isAoOrGaa,
          isExportMyTaskFeaturePresent: shouldShowExportMyTasksButton(),
          isSaveReviewTaskFeaturePresent: reviewTasks.shouldShowSubmitNotice(),
          isPreviewAndSubmitReviewTaskTaskFeaturePresent: reviewTasks.shouldShowSubmitNotice(),
        }),
      });
    }
  }, [isTasksLoadedFirstTime, isUserRoleApiLoaded]);

  let rejectedReviewTasks = [];
  let postReviewTasksLoading = null;
  let updatedReviewTaskIds: Nullable<Array<string>> = null;
  let postReviewTasksError: any = null;
  let postReviewTasksShowPopup = false;
  const updatedReview = useSelector((rootState: RootState) =>
    findSubmittedReviewByReviewId(rootState.appReviewTasks, reviewId),
  );
  let showTaskRejectedReason = true;
  if (updatedReview) {
    updatedReviewTaskIds = updatedReview.reviewTasks.value.updatedIds;
    rejectedReviewTasks = updatedReview.reviewTasks.value.rejected;
    showTaskRejectedReason = updatedReview.reviewTasks.value.showTaskRejectedReason;
    postReviewTasksLoading = updatedReview.reviewTasks.progress;
    postReviewTasksError = updatedReview.reviewTasks.error;
    postReviewTasksShowPopup = updatedReview.showPopup;
  }

  function setFilterConfigurations(selectedFilters: Array<FilterKeys>) {
    if (selectedFilters.length === 0) {
      setFilters(showAllTasksFilterState);
    } else {
      setFilters({
        [FilterKeys.ShowAll]: false,
        [FilterKeys.ShowMyTasks]: selectedFilters.includes(FilterKeys.ShowMyTasks),
        [FilterKeys.ShowSavedDecision]: selectedFilters.includes(FilterKeys.ShowSavedDecision),
        [FilterKeys.ShowPendingDecisions]: selectedFilters.includes(FilterKeys.ShowPendingDecisions),
        [FilterKeys.ShowSubmittedDecisions]: selectedFilters.includes(FilterKeys.ShowSubmittedDecisions),
      });
    }
  }

  const isCommentRequired = (decision, comment, reason) =>
    (decision === "Needs adjustment" || decision === "Needs removal") && (isNullOrEmptyString(comment) || isNullOrUndefined(reason));

  const isActionRequiredWhenCommentIsFilled = (decision, comment, reason) =>
    isNullOrUndefinedOrEmpty(decision) && !(isNullOrEmptyString(comment) || isNullOrUndefined(reason));

  let selectedRowKeys: string[] = [];
  let batchSelectComment = null;
  let batchSelectAction = null;
  let batchSelectDecisionReason = null;
  let batchSelectCommentRequired = false;
  let batchActionRequiredWhenCommentIsFilled = false;
  const selectedReviewTasks = useSelector((rootState: RootState) =>
    findBatchSelectedReviewTaskKeysByReviewId(rootState.appReviewTasks, reviewId),
  );
  if (selectedReviewTasks) {
    selectedRowKeys = selectedReviewTasks.keys;
    batchSelectAction = selectedReviewTasks.action;
    batchSelectComment = selectedReviewTasks.comment;
    batchSelectDecisionReason = selectedReviewTasks.decisionReason;
    batchSelectCommentRequired = isCommentRequired(batchSelectAction, batchSelectComment, batchSelectDecisionReason);
    batchActionRequiredWhenCommentIsFilled = isActionRequiredWhenCommentIsFilled(
      batchSelectAction,
      batchSelectComment,
      batchSelectDecisionReason,
    );
  }

  if (postReviewTasksShowPopup) {
    dispatch(AppReviewTasksActions.disableUpdatedReviewTaskPopup({ reviewId }));
    if (rejectedReviewTasks.length > 0) {
      postReviewTaskType!!.error(dispatch, scrollToLastUpdatedPositionInTable, rejectedReviewTasks.length);
    } else if (!postReviewTasksError) {
      postReviewTaskType?.success(dispatch, scrollToLastUpdatedPositionInTable, updatedReviewTaskIds!!);
    } else if (postReviewTasksError?.message !== "") {
      postReviewTaskType?.errorNotRelatedToReviewTasks(dispatch, scrollToLastUpdatedPositionInTable);
    }
    setPostReviewTaskType(null);
  }

  if (editedReviewTasks && editedReviewTasks.length > 0) {
    reviewTasks.markTasksThatAreEdited(editedReviewTasks, isCommentRequired, isActionRequiredWhenCommentIsFilled);
  }

  useEffect(() => {
    setPreviewReviewTasks((oldReviewTasks) => {
      const updatedReviewTasks = new ReviewTasks(oldReviewTasks.tasks);
      updatedReviewTasks.markTasksThatAreEdited(editedReviewTasks, isCommentRequired, isActionRequiredWhenCommentIsFilled);
      return updatedReviewTasks;
    });
  }, [JSON.stringify(previewReviewTasksPEV)]);

  function onSaveButtonClick() {
    setPostReviewTaskType(new PostBatchSaveReviewTaskAction(appId, reviewId));
    dispatch(
      // @ts-ignore
      AppReviewTasksActions.postReviewTasks({
        appId,
        reviewId,
        payload: reviewTasks.savePayload(reviewId),
      }),// @ts-ignore
    ).then(() => {
      dispatch(AppReviewTasksActions.removeEditedReviewTasks({ reviewId }));
    });
  }

  const onSubmitBtnClick = () => {
    setPostReviewTaskType(new PostBatchSubmitReviewTaskAction(appId, reviewId));
    dispatch(
      // @ts-ignore
      AppReviewTasksActions.postReviewTasks({
        appId,
        reviewId,
        payload: isSaveAsDraftFeatureEnabled
          ? previewReviewTasks.submitPayload(reviewId)
          : reviewTasks.submitPayload(reviewId),
      }),
    );
    removeBatchSelectedReview();
    setPreviewModalVisible(false);
  };

  const upsertEditedReviewTasks = (payload) => {
    dispatch(AppReviewTasksActions.upsertEditedReviewTask({ ...payload, appId }));
  };

  const resetTask = (record) => {
    record.reset();
  };

  const handleClearSaved = (record) => {
    setPostReviewTaskType(new PostBatchClearReviewTaskAction(appId, reviewId));
    dispatch(
      // @ts-ignore
      AppReviewTasksActions.postReviewTasks({
        appId,
        reviewId,
        payload: {
          data: [record.reviewTaskId],
          reviewId: reviewId,
          action: UpdateReviewTaskActions.BatchClearDecisionsRequest,
        },
      }),
    );
  };

  const reassignReviewTasks = async (taskIds, assignee, comments) => {
    const payload = {
      taskIds,
      assignee,
      comments,
      assignor: userDetails?.id,
    };
    setPostReviewTaskType(new PostBatchTasksReassignmentAction(appId, reviewId, payload));
    dispatch(
      // @ts-ignore
      AppReviewTasksActions.postReviewTasks({
        appId,
        reviewId,
        payload: {
          data: payload,
          reviewId: reviewId,
          action: UpdateReviewTaskActions.BatchTasksReassignmentRequest,
        },
      }),
    );
  };

  function tasksPresentInEditedReviewTasksButNotIn(givenTaskIds: Array<string>) {
    return editedReviewTasks.filter((task) => givenTaskIds.includes(task.reviewTaskId)).map((task) => task.reviewTaskId);
  }

  function removeEditedReviewTasks(reviewTaskIds: string[]) {
    dispatch(AppReviewTasksActions.removeEditedReviewTasksByTaskIdsIfExists({ reviewTaskIds }));
  }

  function removeLocalCachedTasksThatAreNotEdited() {
    removeEditedReviewTasks(tasksPresentInEditedReviewTasksButNotIn(reviewTasks.nonEditedTasks()));
  }

  useEffect(() => {
    removeLocalCachedTasksThatAreNotEdited();
  }, [JSON.stringify(reviewTasks.editedReviewTasks())]);

  function removeLocalCachedTasksThatAreNoLongerMyTasks() {
    dispatch(AppReviewTasksActions.removeEditedReviewTasksByTaskIdsIfExists({ reviewTaskIds: tasksPresentInEditedReviewTasksButNotIn(reviewTasks.notMyTasks()) }));
  }

  useEffect(() => {
    removeLocalCachedTasksThatAreNoLongerMyTasks();
  }, [JSON.stringify(reviewTasks.notMyTasks())]);

  useEffect(() => {
    function setMyTasksFilter() {
      if (isMyContent(reviewId) && isNotAllMyContent(reviewId)) {
        dispatch(
          ApplicationDetailsActions.setMyListSwitch({
            appId,
            tabId: reviewId,
            show: true,
            active: true,
          })
        );
      }
      else {
        dispatch(
          ApplicationDetailsActions.setMyListSwitch({
            appId,
            tabId: reviewId,
            show: false,
            active: true,
          })
        );
      }
    }

    if (isNullOrUndefinedOrEmpty(reviewTasks.tasks)) {
      // @ts-ignore
      dispatch(AppReviewTasksActions.getReviewTasks({ appId, reviewId })).then(() => {
        // After loading data for review, check if all review tasks belong to the user, if not enable switch for tasks filtering
        setMyTasksFilter();
        setIsTasksLoadedFirstTime(true);
      });
    }
    else {
      setMyTasksFilter()
    }
  }, [dispatch, appId, reviewId]); // passing empty dependencies will trigger useEffect only on first mount

  useEffect(() => {
    if (showMyListFilter) {
      setFilters(showMyTasksFilterState);
    } else {
      setFilters(showAllTasksFilterState)
    }
  },[showMyListFilter])

  const upsertBatchSelectedReview = (payload) => {
    dispatch(AppReviewTasksActions.upsertBatchSelectedReviewTasksKeys({ reviewId, ...payload }));
  };

  const removeBatchSelectedReview = () => {
    dispatch(AppReviewTasksActions.removeBatchSelectedReviewTasks({ reviewId }));
  };

  const onBatchSelectedSubmit = () => {
    dispatch(AppReviewTasksActions.convertBatchSelectedToBatchEdited({ reviewId }));
    removeBatchSelectedReview();
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedRowKeys, records) => {
      if (selectedRowKeys.length > 0) {
        upsertBatchSelectedReview({ keys: selectedRowKeys, records });
      } else {
        removeBatchSelectedReview();
      }
    },
    getCheckboxProps: (record) => ({
      disabled: !record.hasEditAccess,
      name: record.reviewTaskId, // selected record key
    }),
    fixed: "right",
  };

  async function reassignReviewTask(taskId, assignee, comments) {
    await service
      .reassignReviewTask({ taskId, assignee, comments })
      .then(() => {
        successMsg(AlertMessage.reAssignTask.success);
      })
      .catch((error) => {
        const errorDescription = isNullOrUndefined(error?.response?.data?.errors[0]?.errorMessage)
          ? AlertMessage.reAssignTask.error
          : error?.response?.data?.errors[0]?.errorMessage;
        errorMsg(errorDescription);
      });
    // @ts-ignore
    dispatch(AppReviewTasksActions.getReviewTasks({ appId, reviewId }));
  }

  function setSelectedColumns(shouldResetEmptySelectedColumns: boolean) {
    if (activeTabId === reviewId && columns.filterableColumns().length > 0) {
      selectedColumns = selectedColumns.filter((selectedColumn) => {
          return columns.filterableColumns().some((filteredColumn) => filteredColumn === selectedColumn);
        },
      );
      if(totalColumns.length > 0){
        selectedColumns = selectedColumns.concat(
          columns.filterableColumns().filter((column) => !totalColumns.includes(column))
        );
      }
      if (shouldResetEmptySelectedColumns) {
        selectedColumns = selectedColumns.length > 0 ? selectedColumns : columns.filterableColumns();
      }
      dispatch(
        ApplicationDetailsActions.setActiveColumns({
          appId,
          totalColumns: columns.filterableColumns(),
          pinnedColumns: columns.pinnedColumns(pinnedColumnsList).filter((pinnedColumn) => {
            return !automaticallyRemovedColumns.some((automaticallyRemovedColumn) => {
              return automaticallyRemovedColumn === pinnedColumn;
            });
          }),
          selectedColumns,
        }),
      );
    }
  }

  function fetchPreviewReviewTasks() {
    // @ts-ignore
    dispatch(AppReviewTasksActions.getPreviewReviewTasks({ appId, reviewId }));
  }

  function resetIgnoreFlagForPreviewTasks() {
    const updatedPreviewReviewTasks = previewReviewTasks.resetIgnoredFlagsToDefault();
    setPreviewReviewTasks(updatedPreviewReviewTasks);
  }

  /* istanbul ignore next */
  function resetIgnoredReviewTaskKeys() {
    setIgnoredReviewTasks([]);
    setNoOfIgnoredRows(0);
  }

  useEffect(() => {
      setSelectedColumns(true);
  }, [JSON.stringify(columns), activeTabId]);

  useEffect(() => {
      setSelectedColumns(false);
  }, [automaticallyRemovedColumns]);

  function shouldShowExportMyTasksButton(): boolean {
    return isExportTasksFeatureEnabled && reviewTasks.areTasksAssignedToTheCurrentUser();
  }
  const appInfo = appsList?.find((app) => app.id === appId);

  return {
    columns: columns.columnRepresentations as Array<ColumnRepresentationDTO>,
    reviewTasks,
    isPreviewTableLoading: !!previewReviewTasksPEV.progress,
    upsertEditedReviewTasks,
    isLoading: !!initialReviewLoading || !!postReviewTasksLoading,
    onSubmitBtnClick,
    rejectedReviewTasks,
    rowSelection,
    selectedRowKeys,
    upsertBatchSelectedReview,
    removeBatchSelectedReview,
    batchSelectAction,
    batchSelectComment,
    batchSelectDecisionReason,
    batchSelectCommentRequired,
    batchActionRequiredWhenCommentIsFilled,
    onBatchSelectedSubmit,
    reassignReviewTask,
    resetTask,
    setAutomaticallyRemovedColumns,
    onSaveButtonClick,
    handleClearSaved,
    previewReviewTasks,
    noOfIgnoredRows,
    setNoOfIgnoredRows,
    previewTableRowClassName: (record: ReviewTask) => {
      if (isSaveAsDraftFeatureEnabled) {
        if (record.reviewedBy && (record.reviewedBy !== userDetails.fullName)) {
          return "preview-conflict-row";
        }
      }
      return "";
    },
    previewModalCancelHandler: () => {
      dispatch(AppReviewTasksActions.syncLatestReviewTasks({ reviewId }));
      setPreviewModalVisible(false);
    },
    submitButtonHandler: () => {
      if (isSaveAsDraftFeatureEnabled) {
        // @ts-ignore
        fetchPreviewReviewTasks();
        resetIgnoreFlagForPreviewTasks();
        resetIgnoredReviewTaskKeys();
      }
      setPreviewModalVisible(true);
    },
    isSaveAsDraftFeatureEnabled,
    previewTasksDatasource: isSaveAsDraftFeatureEnabled
      ? previewReviewTasks.submittable()
      : reviewTasks.submittable(),
    userDetails,
    setPreviewReviewTasks,
    ignoredReviewTasks,
    setIgnoredReviewTasks,
    isPreviewModalVisible,
    setPreviewModalVisible,
    countOfTasksAddedByOtherAccessOwners: () => {
      const tasks = previewReviewTasks.submittableTasksNotReviewedBy(userDetails?.fullName);
      return ({
        total: tasks.length,
        noChangeNeeded: tasks.filter((task) => task.decision === "No change needed").length,
        needsAdjustment: tasks.filter((task) => task.decision === "Needs adjustment").length,
        needsRemoval: tasks.filter((task) => task.decision === "Needs removal").length,
      });
    },
    countOfTasksAddedByCurrentUser: () => {
      const tasks = previewReviewTasks.submittableTasksReviewedBy(userDetails?.fullName);
      return ({
        total: tasks.length,
        noChangeNeeded: tasks.filter((task) => task.decision === "No change needed").length,
        needsAdjustment: tasks.filter((task) => task.decision === "Needs adjustment").length,
        needsRemoval: tasks.filter((task) => task.decision === "Needs removal").length,
      });
    },
    setFilterConfigurations,
    filters,
    removeEditedReviewTasks,
    isSubmitButtonDisabled: previewReviewTasks.submittable().length === 0,
    showMyListFilter,
    showExportMyTasksButton: shouldShowExportMyTasksButton(),
    onSelectUserGroupCheckBox: (rowKeys: string[]) => {
      const allRowKeys = uniq(selectedRowKeys.concat(rowKeys));
      const editableTasks = reviewTasks.findAllBy(allRowKeys).editableTasks();
      upsertBatchSelectedReview({ keys: editableTasks.map((it) => it.key), records: editableTasks });
    },
    onUnSelectUserGroupCheckBox: (rowKeys: string[]) => {
      const selectKeysForOtherUserGroups = difference(selectedRowKeys, rowKeys);
      upsertBatchSelectedReview({
        keys: selectKeysForOtherUserGroups,
        records: reviewTasks.findAllBy(selectKeysForOtherUserGroups),
      });
    },
    isUserGroupCheckboxDisabled: (rowKeys: string[]) => reviewTasks.findAllBy(rowKeys).hasNoEditableTasks(),
    setHoveredTaskIds,
    hoveredTaskIds,
    reviewInFullScreenFeatureEnabled,
    tourState,
    reassignReviewTasks,
    showTaskRejectedReason,
    setTourState,
    appInfo,
    isAoOrGaa
  };
};
